import {useCallback, useEffect, useMemo, useState} from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  Grid,
  InputLabel,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
  IconButton,
  MenuItem,
} from '@mui/material';
import {DataGridPro} from '@mui/x-data-grid-pro';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import {Link} from 'react-router-dom';
import {
  GridColDef,
  GridRenderCellParams,
  GridSortModel,
} from '@mui/x-data-grid';
import {DataGridCustPagination} from '../../../../components/utlis/DataGrid/Pagination';
import Waveform from '../../../wavesurfer/WaveForm';
import {useRqGroupedGenres} from '../../../../react_query/music_genres/ListGenresGrouped';
import { defaultDataGridProSearchSort } from '../../../../dto/Html.dto';
import {useStoreSelector} from '../../../../store/configstore';
import {companyIdSelector} from '../../../../store/Slices/companySlice';
import {IMediaTracksQueryParams, useRqMediaTracksPaginate} from '../../../../react_query/media_tracks/ListMediaTrackPaginatPro';
import {useStationContext} from '../../../../components/models/stations/StationContext';
import MusicPreviewBtn from '../../../../components/music/MusicPreviewBtn';
import {IMediaTrack} from '../../../../dto/MediaTrack.dto';
import {GridDurationColumn} from '../../../../components/grid/GridColumns';
import {apiGet} from '../../../../../src/axiosConfig';
import {
  ISpecialtyCategoryMediaTracksQueryParams,
  useRqSpecialtyCategoryPaginateMediaTracks,
} from '../../../../react_query/Speciality_Category/LIstSCMediaTrackPagination';
import IconBack from '../../../../asset/icons/icon_back.svg';
import TrackFilterSelect, {Genre} from './TrackFilterSelect';
import {useRqPlaylistList} from '../../../../react_query/media_tracks/on_demand_playlists/ListPlaylist';
import {useRqPostCustomLibraryTracks} from '../../../../react_query/custom_libarary/ListCLMediaTrackPaginate';
import {IStation} from '../../../../dto/Station.dto';
import {useRqListCompanyStations} from '../../../../react_query/stations/CompanyStationList';
import FuzzySearchSong from '../../search/FuzzySearchSong';

interface IMediaTrackProp {
  id: number;
  track_artist: string;
  track_name: string;
  url: string;
}
interface CreateClipMusicSelectionProps {
  clipType?: string;
  walkupType?: string;
  clipResetTab?: () => void;
  handleCloseViewModal?: () => void;
}
const CreateClipMusicSelection = ({
  clipType = '',
  walkupType = '',
  clipResetTab,
  handleCloseViewModal,
}: CreateClipMusicSelectionProps) => {
  const {stations, setStations} = useStationContext();

  const [mediaTrack, setMediaTrack] = useState<IMediaTrackProp[]>([]);
  const [trackdata, setTrackData] = useState({
    id: 0,
    task_id: 0,
    title: '',
    artist: '',
    url: '',
  });
  const [query, setQuery] = useState<IMediaTracksQueryParams>({
    ...defaultDataGridProSearchSort,
    exclude_noplay: true,
  });
  const [specialty, setSpecialty] = useState<Genre[]>([]);
  const [uiSearchType, setUiSearchType] = useState('music-library');
  const [specialtyFilterId, setSpecialtyFilterId] = useState(0);
  const [customFilterId, setCustomFilterId] = useState<number[]>([]);
  const [sponsorCommercial, setSponsorCommercial] = useState<Genre[]>([]);
  const [specialtyQuery, specialtySetQuery] =
    useState<ISpecialtyCategoryMediaTracksQueryParams>({
      per_page: 10,
      page: 0,
    });
  const companyId = useStoreSelector(companyIdSelector);
  const {data: stationData} = useRqListCompanyStations(Number(companyId), true);

  useEffect(() => {
    if (stationData && stationData.length > 0) {
      setStations(stationData as IStation[]);
    }
  }, [stationData, setStations]);

  const {data, isFetched, isError} = useRqGroupedGenres(companyId);
  const {
    data: mediaData,
    isLoading: mediaIsLoading,
    isFetched: mediaIsFetched,
    isError: mediaIsError,
  } = useRqMediaTracksPaginate(
    query,
    companyId,
    uiSearchType === 'music-library',
  );

  const {data: specialtyTrack} = useRqSpecialtyCategoryPaginateMediaTracks(
    companyId,
    specialtyFilterId,
    specialtyQuery,
    Boolean(
      companyId &&
        specialtyFilterId >= 0 &&
        uiSearchType === 'speciality-category',
    ),
  );
  const stationIds = stations.map(station => station.id);

  const {data: customLibrary, isFetching: customIsFetching} = useRqPlaylistList(
    companyId,
    {
      station_ids: stationIds,
      hidden: 0,
    },
    'custom-library',
    uiSearchType !== 'speciality-category' &&
      uiSearchType !== 'music-library' &&
      !!stationIds.length,
  );

  const {data: customTrack, isFetching: customFetching} =
    useRqPostCustomLibraryTracks(
      companyId,
      {
        customLibraryIds: customFilterId,
        ...specialtyQuery,
      },
      Boolean(
        companyId &&
          customFilterId.length > 0 &&
          !customFilterId.includes(-1) &&
          uiSearchType === 'custom-library',
      ),
    );

  const isSpecialOrCustom =
    uiSearchType === 'custom-library' || uiSearchType === 'speciality-category';

  const isCustomLibraryExists = useMemo(() => {
    if (!customIsFetching && customLibrary) {
      return customLibrary.data.some(o => o.id > 1);
    }
    return stationIds.length;
  }, [customIsFetching, customLibrary, stationIds.length]);

  const handlePagination = ({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
  }) => {
    setQuery(prev => ({...prev, page, pageSize}));
    if (isSpecialOrCustom) {
      specialtySetQuery(prev => ({
        ...prev,
        page: page,
        per_page: pageSize,
      }));
    }
  };

  const handleSortModelChange = (model: GridSortModel) => {
    setQuery(prev => ({...prev, sort: model}));
  };

  const pushTracks = (tracks?: IMediaTrackProp[]) => {
    if (!tracks) {
      setMediaTrack([]);
      return;
    }
    const formatted = tracks.map(({id, track_artist, track_name, url}) => ({
      id,
      track_artist,
      track_name,
      url,
    }));
    setMediaTrack(formatted.length ? formatted : []);
  };

  useEffect(() => {
    switch (uiSearchType) {
      case 'music-library':
        if (mediaIsFetched && !mediaIsError && mediaData) {
          pushTracks(mediaData.data);
        }
        break;

      case 'speciality-category':
        if (specialtyTrack) {
          pushTracks(specialtyTrack.data);
        }
        break;

      case 'custom-library':
        if (!customIsFetching && customLibrary) {
          const mapped = customLibrary.data.map(item => ({
            id: item.id,
            name: item.name,
          }));
          setSponsorCommercial(mapped);

          setCustomFilterId(prev => {
            if ((!prev || prev.length === 0) && mapped.length > 0) {
              return mapped.filter(item => item.id > 0).map(item => item.id);
            }
            return prev;
          });

          if (!customFetching) {
            pushTracks(customTrack?.data);
          }
        }
        break;

      default:
        break;
    }
  }, [
    uiSearchType,
    mediaIsFetched,
    mediaIsError,
    mediaData,
    specialtyTrack,
    customIsFetching,
    customLibrary,
    customTrack,
    customFetching,
  ]);

  useEffect(() => {
    setQuery(prev => ({...prev, page: 0}));
    specialtySetQuery(prev => ({...prev, page: 0}));
  }, [uiSearchType]);

  const handleSpecialtyFilterChange = useCallback((id: number) => {
    setSpecialtyFilterId(id);
    setQuery(prev => ({...prev, page: 0}));
    specialtySetQuery(prev => ({...prev, page: 0}));
  }, []);

  const handleCustomFilterChange = useCallback((ids: number[]) => {
    setCustomFilterId(ids);
    setQuery(prev => ({...prev, page: 0}));
    specialtySetQuery(prev => ({...prev, page: 0}));
  }, []);

  const handleSelect = (params: GridRenderCellParams) => {
    setTrackData({
      id: params.row.id,
      task_id: 0,
      title: params.row.track_name,
      artist: params.row.track_artist,
      url: params.row.url,
    });
  };

  const columns: GridColDef[] = [
    {
      field: 'track_artist',
      headerName: 'Artist Name',
      flex: 1,
    },
    {
      field: 'track_name',
      headerName: 'Song Title',
      flex: 1,
    },
    GridDurationColumn,
    {
      field: 'id',
      type: 'actions',
      headerName: 'Play',
      width: 54,
      renderCell: (params: GridRenderCellParams<IMediaTrack>) =>
        params.row.url ? (
          <MusicPreviewBtn
            id={params.row.id}
            url={params.row.url}
            track_name={params.row.track_name}
            track_artist={params.row.track_artist}
          />
        ) : null,
    },
    {
      field: 'action',
      headerName: 'Action',
      sortable: false,
      hideable: false,
      flex: 1,
      renderCell: params => {
        return (
          <Button
            variant='outlined'
            className='blue-variant19'
            onClick={() => handleSelect(params)}>
            Click to select
          </Button>
        );
      },
    },
  ];

  const handleSpecialtySearch = useCallback(
    (e: SelectChangeEvent<string>) => {
      const value = e.target.value;
      setUiSearchType(value);
      const resetSearch = () => setQuery(prev => ({...prev, search: ''}));

      switch (value) {
        case 'music-library':
          setQuery(prev => ({...prev, genres: [], search: ''}));
          break;

        case 'speciality-category':
          apiGet(`v1/company/${companyId}/speciality-category`).then(res =>
            setSpecialty(res.data.data),
          );
          resetSearch();
          setSpecialtyFilterId(0);
          break;

        default:
          setQuery(prev => ({...prev, search: value}));
      }
    },
    [companyId, setUiSearchType, setQuery, setSpecialty],
  );

  const handleGenreChange = (event: SelectChangeEvent<string>) => {
    if (event.target.value && event.target.value !== '0') {
      setQuery(prev => ({
        ...prev,
        genres: [Number(event.target.value)],
        page: 0,
      }));
    } else {
      setQuery(prev => {
        if (prev) {
          const data = {...prev};
          delete data.genres;
          return {...data, page: 0};
        }
        return prev;
      });
    }
  };

  const isLoading =
    (uiSearchType === 'music-library' && mediaIsLoading) ||
    (uiSearchType === 'speciality-category' && customFetching) ||
    (uiSearchType === 'custom-library' && customFetching);

  const handleFuzzyChange = useCallback(
      (filters: {track_artist: string; track_name: string; fuzzy: boolean}) => {
        setQuery((prev: IMediaTracksQueryParams) => ({
          ...prev,
          ...filters,
          page: 0,
        }));
      },
      [setQuery],
    );

  const handleFuzzyReset = useCallback(() => {
    setQuery((prev: IMediaTracksQueryParams) => ({
      ...prev,
      track_artist: '',
      track_name: '',
      fuzzy: false,
      page: 0,
    }));
  }, []);

  return (
    <Stack>
      {clipType !== 'customClip' && (
        <Grid
          container
          className='h-80 flex flex-row items-center bottomBorderSolid'>
          <Grid item xs={5}>
            <Link to='/music/on-demand' className='flex pl-15'>
              <img src={IconBack} className='mr-3' alt='' />
              <Typography component='h6' className='text-white-50 font-bold'>
                Back to Music
              </Typography>
            </Link>
          </Grid>
          <Grid
            item
            xs={7}
            className='text-center flex items-center justify-start'>
            <IconButton>
              <ContentCutIcon />
            </IconButton>
            <Typography component='h4' className='font-bold text-white'>
              Create Clip
            </Typography>
          </Grid>
        </Grid>
      )}

      <Grid container className='pl-15'>
        <Grid item xs={5} sx={{height: '79vh'}}>
          <Card sx={{height: '100%'}}>
            <CardContent>
              <Stack className='flex flex-row justify-between my-1 mx-0'>
                <FormControl fullWidth>
                  <InputLabel id='source-label'>Source</InputLabel>
                  <Select
                    labelId='source-label'
                    id='source-select'
                    value={uiSearchType}
                    onChange={handleSpecialtySearch}
                    label='Source'>
                    <MenuItem value='music-library'>Music Library</MenuItem>
                    <MenuItem
                      value='custom-library'
                      disabled={!isCustomLibraryExists}>
                      Single Play Library
                    </MenuItem>
                    <MenuItem value='speciality-category'>
                      Specialty Category
                    </MenuItem>
                  </Select>
                </FormControl>
                <TrackFilterSelect
                  uiSearchType={uiSearchType}
                  genresData={data || []}
                  isFetched={isFetched}
                  isError={isError}
                  speciality={specialty}
                  sponserCommercial={sponsorCommercial}
                  onGenreChange={handleGenreChange}
                  setSpecialityFilterId={handleSpecialtyFilterChange}
                  setCustomFilterId={handleCustomFilterChange}
                />
              </Stack>
              <FuzzySearchSong
                onReset={handleFuzzyReset}
                onFuzzySearchChange={handleFuzzyChange}
                trackArtist={query.track_artist ?? ''}
                trackName={query.track_name ?? ''}
              />
              <Box sx={{mt: 1, height: '55vh'}}>
                <DataGridPro
                  disableColumnSelector
                  className='custom-data-grid'
                  rows={mediaTrack || []}
                  columns={columns}
                  loading={isLoading}
                  rowCount={
                    mediaTrack.length > 0
                      ? uiSearchType === 'custom-library'
                        ? customTrack?.meta?.total ?? 0
                        : uiSearchType === 'speciality-category'
                        ? specialtyTrack?.meta?.total ?? 0
                        : mediaData?.total ?? 0
                      : 0
                  }
                  paginationModel={{page: query.page, pageSize: query.pageSize}}
                  pageSizeOptions={[10, 15, 25]}
                  paginationMode='server'
                  pagination={true}
                  onPaginationModelChange={handlePagination}
                  onSortModelChange={handleSortModelChange}
                  disableRowSelectionOnClick
                  slots={{
                    pagination: DataGridCustPagination,
                  }}
                  slotProps={{
                    columnsPanel: {
                      disableHideAllButton: true,
                      disableShowAllButton: true,
                    },
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item container xs={7} className='pr-10'>
          <Card sx={{width: '100%'}}>
            <CardContent className='blue-variant29 custom-clip-height'>
              <Waveform
                data={trackdata}
                type='clip'
                walkupType={walkupType}
                clipType={clipType}
                clipResetTab={clipResetTab}
                handleCloseViewModal={handleCloseViewModal}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Stack>
  );
};

export default CreateClipMusicSelection;
